// /app/frontend/src/components/admin/RoleDashboard.jsx
// Role-based dashboard with customized metrics and quick actions per role

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { PageHealthAlerts } from './website/PageHealthAlerts';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import RevenueDashboard from './RevenueDashboard';
import SmartReEngagement from './SmartReEngagement';
import ReviewTracker from './ReviewTracker';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  FileText, Package, Receipt, DollarSign, Users, Calendar,
  Boxes, Truck, TrendingUp, TrendingDown, Clock, CheckCircle2,
  AlertCircle, ArrowRight, BarChart3, Wrench, Building2,
  ShoppingCart, UserCheck, Mail, Activity, Target, Briefcase,
  MapPin, Phone, ChevronLeft, ChevronRight, Navigation, Car, Play, CheckCircle, Camera, Image,
  Trophy, Sparkles, Settings2, GripVertical, Eye, EyeOff, X as XIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import TechnicianLeaderboard from './TechnicianLeaderboard';
import SmartScheduling from './SmartScheduling';
import InstallerRouteMap from './InstallerRouteMap';
import AdminQuickStats from './AdminQuickStats';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UnlinkedRequestsCounts } from './UnlinkedRequestsCounts';
import { NeedsPricingCard } from './NeedsPricingCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Dashboard widget configurations per role
const ROLE_WIDGETS = {
  role_super_admin: ['overview', 'sales_pipeline', 'revenue', 'team_performance', 'inventory_alerts', 'recent_activity'],
  role_admin: ['overview', 'sales_pipeline', 'revenue', 'team_performance', 'inventory_alerts', 'recent_activity'],
  role_sales_manager: ['team_sales', 'sales_pipeline', 'my_team', 'top_customers', 'pending_approvals'],
  role_sales_rep: ['my_targets', 'my_quotes', 'my_orders', 'my_customers', 'my_schedule'],
  role_production_manager: ['production_queue', 'inventory_status', 'pending_orders', 'supplier_orders', 'low_stock'],
  role_installer: ['my_schedule', 'assigned_projects', 'upcoming_installations', 'completed_today'],
  role_accounting: ['revenue_summary', 'outstanding_invoices', 'recent_payments', 'overdue_invoices', 'monthly_report'],
  role_customer_service: ['recent_inquiries', 'pending_quotes', 'order_status', 'customer_issues'],
  role_trade_partner: ['my_orders', 'my_quotes', 'my_invoices', 'account_status', 'discounts'],
};

// Quick actions per role
const ROLE_ACTIONS = {
  role_super_admin: [
    { label: 'Create Quote', icon: FileText, action: 'quotes' },
    { label: 'Manage Users', icon: Users, action: 'company-users' },
    { label: 'View Reports', icon: BarChart3, action: 'product-sales' },
    { label: 'Send Email', icon: Mail, action: 'mails' },
  ],
  role_admin: [
    { label: 'Create Quote', icon: FileText, action: 'quotes' },
    { label: 'View Schedule', icon: Calendar, action: 'schedule' },
    { label: 'Manage Inventory', icon: Boxes, action: 'inventory' },
    { label: 'View Reports', icon: BarChart3, action: 'product-sales' },
  ],
  role_sales_manager: [
    { label: 'Create Quote', icon: FileText, action: 'quotes' },
    { label: 'View Pipeline', icon: TrendingUp, action: 'quotes' },
    { label: 'Team Schedule', icon: Calendar, action: 'schedule' },
    { label: 'Approve Orders', icon: CheckCircle2, action: 'sales-orders' },
  ],
  role_sales_rep: [
    { label: 'New Quote', icon: FileText, action: 'quotes' },
    { label: 'My Customers', icon: Users, action: 'customers' },
    { label: 'My Schedule', icon: Calendar, action: 'schedule' },
    { label: 'View Orders', icon: Package, action: 'sales-orders' },
  ],
  role_production_manager: [
    { label: 'Inventory', icon: Boxes, action: 'inventory' },
    { label: 'Suppliers', icon: Truck, action: 'suppliers' },
    { label: 'Pending Orders', icon: Package, action: 'sales-orders' },
    { label: 'Create PO', icon: ShoppingCart, action: 'suppliers' },
  ],
  role_installer: [
    { label: 'My Schedule', icon: Calendar, action: 'schedule' },
    { label: 'Projects', icon: Wrench, action: 'projects' },
    { label: 'Today\'s Jobs', icon: CheckCircle2, action: 'schedule' },
  ],
  role_accounting: [
    { label: 'Invoices', icon: Receipt, action: 'invoices' },
    { label: 'Payments', icon: DollarSign, action: 'payments' },
    { label: 'Reports', icon: BarChart3, action: 'product-sales' },
    { label: 'Overdue', icon: AlertCircle, action: 'invoices' },
  ],
  role_customer_service: [
    { label: 'Customers', icon: Users, action: 'customers' },
    { label: 'View Quotes', icon: FileText, action: 'quotes' },
    { label: 'Order Status', icon: Package, action: 'sales-orders' },
  ],
  role_trade_partner: [
    { label: 'New Quote', icon: FileText, action: 'quotes' },
    { label: 'My Orders', icon: Package, action: 'sales-orders' },
    { label: 'My Invoices', icon: Receipt, action: 'invoices' },
  ],
};

// Dashboard section definitions for customization
const DASHBOARD_SECTIONS = {
  quick_stats: { label: 'Quick Stats', icon: BarChart3, roles: ['role_super_admin', 'role_admin', 'role_sales_manager', 'role_accounting'] },
  quick_actions: { label: 'Quick Actions', icon: Activity, roles: ['all'] },
  widget_grid: { label: 'Metrics Widgets', icon: Target, roles: ['all'] },
  leaderboard: { label: 'Technician Leaderboard', icon: Trophy, roles: ['role_super_admin', 'role_admin', 'role_sales_manager'] },
  smart_scheduling: { label: 'Smart Scheduling', icon: Calendar, roles: ['role_super_admin', 'role_admin', 'role_sales_manager'] },
  revenue: { label: 'Revenue Dashboard', icon: DollarSign, roles: ['role_super_admin', 'role_admin', 'role_sales_manager', 'role_accounting'] },
  re_engagement: { label: 'Smart Re-Engagement', icon: Mail, roles: ['role_super_admin', 'role_admin', 'role_sales_manager'] },
  review_tracker: { label: 'Review Tracker', icon: CheckCircle, roles: ['role_super_admin', 'role_admin', 'role_sales_manager'] },
};

const DEFAULT_ORDER = ['quick_stats', 'quick_actions', 'widget_grid', 'leaderboard', 'smart_scheduling', 'revenue', 're_engagement', 'review_tracker'];

function SortableSection({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div {...listeners} className="absolute left-0 top-3 z-10 cursor-grab active:cursor-grabbing p-1 rounded opacity-30 hover:opacity-80 transition-opacity" data-testid={`drag-handle-${id}`}>
        <GripVertical className="w-4 h-4 text-neutral-400" />
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );
}

export default function RoleDashboard({ onNavigate }) {
  const [userPermissions, setUserPermissions] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [activeJobIndex, setActiveJobIndex] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [jobPhotos, setJobPhotos] = useState({});
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_ORDER);
  const [hiddenSections, setHiddenSections] = useState([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch user permissions to determine role
      const permRes = await axios.get(`${API}/api/admin/my-permissions`, { headers });
      setUserPermissions(permRes.data);

      // Fetch stats based on role
      const roleId = permRes.data.role?.id || 'role_admin';
      await fetchRoleSpecificData(roleId);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load dashboard preferences
  useEffect(() => {
    axios.get(`${API}/api/admin/dashboard-preferences`, { headers })
      .then(res => {
        if (res.data?.widgets?.length) setSectionOrder(res.data.widgets);
        if (res.data?.hidden) setHiddenSections(res.data.hidden);
      }).catch(() => {});
  }, []);

  const savePrefs = useCallback((order, hidden) => {
    axios.put(`${API}/api/admin/dashboard-preferences`, { widgets: order, hidden }, { headers }).catch(() => {});
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSectionOrder(prev => {
      const newOrder = arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id));
      savePrefs(newOrder, hiddenSections);
      return newOrder;
    });
  };

  const toggleSection = (id) => {
    setHiddenSections(prev => {
      const next = prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id];
      savePrefs(sectionOrder, next);
      return next;
    });
  };

  const resetLayout = () => {
    setSectionOrder(DEFAULT_ORDER);
    setHiddenSections([]);
    savePrefs(DEFAULT_ORDER, []);
    toast.success('Dashboard reset to default');
    setShowCustomize(false);
  };

  const fetchRoleSpecificData = async (roleId) => {
    const newStats = {};

    try {
      // Fetch quotes stats
      if (['role_super_admin', 'role_admin', 'role_sales_manager', 'role_sales_rep', 'role_customer_service', 'role_trade_partner'].includes(roleId)) {
        const quotesRes = await axios.get(`${API}/api/admin/quotes`, { headers });
        const quotes = quotesRes.data || [];
        newStats.totalQuotes = quotes.length;
        newStats.pendingQuotes = quotes.filter(q => q.status === 'pending' || q.status === 'draft').length;
        newStats.approvedQuotes = quotes.filter(q => q.status === 'approved').length;
        newStats.quotesValue = quotes.reduce((sum, q) => sum + (q.total || 0), 0);
      }

      // Fetch sales orders stats
      if (['role_super_admin', 'role_admin', 'role_sales_manager', 'role_sales_rep', 'role_production_manager', 'role_customer_service', 'role_trade_partner'].includes(roleId)) {
        const ordersRes = await axios.get(`${API}/api/admin/sales-orders`, { headers });
        const orders = ordersRes.data || [];
        newStats.totalOrders = orders.length;
        newStats.pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'in_production').length;
        newStats.completedOrders = orders.filter(o => o.status === 'completed' || o.status === 'delivered').length;
        newStats.ordersValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
      }

      // Fetch invoices stats
      if (['role_super_admin', 'role_admin', 'role_accounting', 'role_trade_partner'].includes(roleId)) {
        const invoicesRes = await axios.get(`${API}/api/admin/invoices`, { headers });
        const invoices = invoicesRes.data || [];
        newStats.totalInvoices = invoices.length;
        newStats.paidInvoices = invoices.filter(i => i.status === 'paid').length;
        newStats.unpaidInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'draft').length;
        newStats.overdueInvoices = invoices.filter(i => i.status === 'overdue').length;
        newStats.invoicesValue = invoices.reduce((sum, i) => sum + (i.total || 0), 0);
        newStats.paidValue = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0);
      }

      // Fetch inventory stats
      if (['role_super_admin', 'role_admin', 'role_production_manager'].includes(roleId)) {
        const inventoryRes = await axios.get(`${API}/api/admin/inventory`, { headers });
        const items = inventoryRes.data || [];
        newStats.totalInventoryItems = items.length;
        newStats.lowStockItems = items.filter(i => (i.stock_level || i.quantity) <= (i.reorder_point || i.min_stock_level)).length;
        newStats.outOfStockItems = items.filter(i => (i.stock_level || i.quantity) === 0).length;
        
        // Fetch detailed inventory alerts
        try {
          const alertsRes = await axios.get(`${API}/api/admin/inventory/alerts`, { headers });
          setInventoryAlerts(alertsRes.data?.alerts || []);
          newStats.criticalAlerts = alertsRes.data?.critical_count || 0;
          newStats.warningAlerts = alertsRes.data?.warning_count || 0;
        } catch (e) {
          console.log('Inventory alerts endpoint not available');
        }
      }

      // Fetch projects stats (all roles use /api/admin/projects which has built-in role filtering)
      if (['role_super_admin', 'role_admin', 'role_sales_manager', 'role_sales_rep', 'role_installer'].includes(roleId)) {
        const projectsRes = await axios.get(`${API}/api/admin/projects`, { headers });
        const projects = projectsRes.data || [];
        newStats.totalProjects = projects.length;
        newStats.activeProjects = projects.filter(p => p.status === 'in_progress' || p.status === 'scheduled').length;
        newStats.completedProjects = projects.filter(p => p.status === 'completed').length;
      }

      // Fetch customers stats
      if (['role_super_admin', 'role_admin', 'role_sales_manager', 'role_sales_rep', 'role_customer_service'].includes(roleId)) {
        const customersRes = await axios.get(`${API}/api/admin/customers`, { headers });
        const customers = customersRes.data || [];
        newStats.totalCustomers = customers.length;
        newStats.newCustomersThisMonth = customers.filter(c => {
          const created = new Date(c.created_at);
          const now = new Date();
          return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
        }).length;
      }

      // Fetch team stats (for managers)
      if (['role_super_admin', 'role_admin', 'role_sales_manager'].includes(roleId)) {
        const usersRes = await axios.get(`${API}/api/admin/company-users`, { headers });
        newStats.totalTeamMembers = (usersRes.data || []).filter(u => u.is_active).length;
      }

      // Fetch activity logs
      if (['role_super_admin', 'role_admin'].includes(roleId)) {
        const logsRes = await axios.get(`${API}/api/admin/activity-logs?limit=10`, { headers });
        setRecentActivity(logsRes.data || []);
      }

      // Fetch today's jobs for installers (use /api/my-schedule which filters by assigned installer)
      if (['role_installer'].includes(roleId)) {
        try {
          const scheduleRes = await axios.get(`${API}/api/my-schedule`, { headers });
          setTodaysJobs(scheduleRes.data || []);
        } catch (e) {
          console.error('Failed to fetch schedule:', e);
        }
      }

    } catch (error) {
      console.error('Error fetching role-specific data:', error);
    }

    setStats(newStats);
  };

  // Update job status with GPS location capture
  const updateJobStatus = async (eventId, newStatus) => {
    setUpdatingStatus(true);
    
    // Materials pre-check before starting job
    if (newStatus === 'in_progress') {
      try {
        const checkRes = await axios.get(`${API}/api/schedule/${eventId}/materials-check`, { headers });
        if (checkRes.data.status === 'warning' && checkRes.data.warnings?.length > 0) {
          const warnings = checkRes.data.warnings;
          const message = warnings.map(w => w.message).join('\n');
          const proceed = window.confirm(
            `⚠️ Material Availability Warning:\n\n${message}\n\nDo you want to start the job anyway?`
          );
          if (!proceed) {
            setUpdatingStatus(false);
            return;
          }
        }
      } catch (err) {
        console.log('Materials check skipped:', err.message);
        // Continue even if check fails
      }
    }
    
    // Capture GPS coordinates
    let locationData = {};
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          });
        });
        locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        console.log('GPS captured:', locationData);
      } catch (geoError) {
        console.log('GPS not available:', geoError.message);
        // Continue without location - not critical
      }
    }
    
    try {
      const response = await axios.patch(
        `${API}/api/schedule/${eventId}/status`,
        { 
          status: newStatus,
          ...locationData
        },
        { headers }
      );
      
      // Update local state
      setTodaysJobs(prev => prev.map(job => 
        job.id === eventId ? { ...job, status: newStatus } : job
      ));
      
      const statusMessages = {
        'en_route': '🚗 You are now en route! Customer notified with tracking link.',
        'in_progress': '🔧 Job started!',
        'completed': '✅ Job completed! Feedback survey sent to customer.'
      };
      toast.success(statusMessages[newStatus] || `Status updated to ${newStatus}`);
      
      // Show location capture status
      if (response.data?.location_captured) {
        toast.info('📍 Location recorded');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Get next status action for a job
  const getNextStatusAction = (currentStatus) => {
    const statusFlow = {
      'scheduled': { next: 'en_route', label: 'Start Route', icon: Car, color: 'bg-blue-600' },
      'en_route': { next: 'in_progress', label: 'Start Job', icon: Play, color: 'bg-amber-600' },
      'in_progress': { next: 'completed', label: 'Complete', icon: CheckCircle, color: 'bg-green-600' },
      'completed': null
    };
    return statusFlow[currentStatus] || statusFlow['scheduled'];
  };

  // Upload job photo
  const uploadJobPhoto = async (eventId, file, photoType) => {
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', photoType);
      
      const response = await axios.post(
        `${API}/api/schedule/${eventId}/photos`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`${photoType === 'before' ? 'Before' : 'After'} photo uploaded!`);
      
      // Update local photos state
      setJobPhotos(prev => ({
        ...prev,
        [eventId]: {
          ...prev[eventId],
          [photoType]: [...(prev[eventId]?.[photoType] || []), response.data.photo_id]
        }
      }));
      
      // Fetch updated photos
      fetchJobPhotos(eventId);
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Fetch photos for a job
  const fetchJobPhotos = async (eventId) => {
    try {
      const response = await axios.get(`${API}/api/schedule/${eventId}/photos`, { headers });
      setJobPhotos(prev => ({
        ...prev,
        [eventId]: {
          before: response.data.before_photos || [],
          after: response.data.after_photos || []
        }
      }));
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Time-based greeting
  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Role-specific welcome messages
  const getRoleWelcomeMessage = (role) => {
    const messages = {
      'role_super_admin': "Full system access • Manage everything",
      'role_admin': "Operations dashboard • All systems go",
      'role_sales_manager': "Team performance at a glance",
      'role_sales_rep': "Your sales pipeline awaits",
      'role_production_manager': "Production & inventory overview",
      'role_installer': "Your schedule & assigned jobs",
      'role_accounting': "Financial overview & invoices",
      'role_customer_service': "Customer inquiries & support",
      'role_trade_partner': "Your orders & account status"
    };
    return messages[role] || "Here's your personalized dashboard";
  };

  // Render role-specific KPI cards at the top
  const renderRoleKPIs = (role) => {
    const kpiConfigs = {
      'role_installer': [
        { label: "Today's Jobs", value: stats.activeProjects || 0, icon: Calendar, color: 'blue', subtext: 'scheduled' },
        { label: "Active Projects", value: stats.activeProjects || 0, icon: Wrench, color: 'amber', subtext: 'assigned to you' },
        { label: "Completed", value: stats.completedProjects || 0, icon: CheckCircle2, color: 'emerald', subtext: 'this month' },
      ],
      'role_sales_rep': [
        { label: "My Quotes", value: stats.totalQuotes || 0, icon: FileText, color: 'blue', subtext: `${stats.pendingQuotes || 0} pending` },
        { label: "Pipeline Value", value: formatCurrency(stats.quotesValue), icon: TrendingUp, color: 'emerald', subtext: 'total quotes' },
        { label: "My Customers", value: stats.totalCustomers || 0, icon: Users, color: 'purple', subtext: `+${stats.newCustomersThisMonth || 0} new` },
      ],
      'role_accounting': [
        { label: "Unpaid Invoices", value: stats.unpaidInvoices || 0, icon: AlertCircle, color: 'amber', subtext: 'need attention' },
        { label: "Overdue", value: stats.overdueInvoices || 0, icon: Clock, color: 'red', subtext: 'past due date' },
        { label: "Collected", value: formatCurrency(stats.paidValue), icon: DollarSign, color: 'emerald', subtext: 'this period' },
      ],
      'role_production_manager': [
        { label: "Low Stock", value: stats.lowStockItems || 0, icon: AlertCircle, color: 'amber', subtext: 'need reorder' },
        { label: "Out of Stock", value: stats.outOfStockItems || 0, icon: Boxes, color: 'red', subtext: 'urgent' },
        { label: "Pending Orders", value: stats.pendingOrders || 0, icon: Package, color: 'blue', subtext: 'in production' },
      ]
    };

    const kpis = kpiConfigs[role];
    if (!kpis) return null;

    const colorClasses = {
      blue: 'from-blue-50 to-blue-100 text-blue-600 border-blue-200',
      emerald: 'from-emerald-50 to-emerald-100 text-emerald-600 border-emerald-200',
      amber: 'from-amber-50 to-amber-100 text-amber-600 border-amber-200',
      purple: 'from-purple-50 to-purple-100 text-purple-600 border-purple-200',
      red: 'from-red-50 to-red-100 text-red-600 border-red-200',
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="role-kpis">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className={`bg-gradient-to-br ${colorClasses[kpi.color]} border`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-80">{kpi.label}</p>
                  <p className="text-2xl font-bold mt-1">{kpi.value}</p>
                  <p className="text-xs opacity-70 mt-1">{kpi.subtext}</p>
                </div>
                <kpi.icon className="h-10 w-10 opacity-30" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const roleId = userPermissions?.role?.id || 'role_admin';
  const roleName = userPermissions?.role?.name || 'Admin';
  const widgets = ROLE_WIDGETS[roleId] || ROLE_WIDGETS.role_admin;
  const actions = ROLE_ACTIONS[roleId] || ROLE_ACTIONS.role_admin;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  // Render widgets based on role
  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'overview':
        return (
          <Card key={widgetId} className="col-span-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                Business Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <div className="text-sm text-blue-600 font-medium">Total Quotes</div>
                  <div className="text-2xl font-bold text-blue-700">{stats.totalQuotes || 0}</div>
                  <div className="text-xs text-blue-500 mt-1">{stats.pendingQuotes || 0} pending</div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                  <div className="text-sm text-emerald-600 font-medium">Sales Orders</div>
                  <div className="text-2xl font-bold text-emerald-700">{stats.totalOrders || 0}</div>
                  <div className="text-xs text-emerald-500 mt-1">{stats.pendingOrders || 0} in progress</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                  <div className="text-sm text-purple-600 font-medium">Revenue</div>
                  <div className="text-2xl font-bold text-purple-700">{formatCurrency(stats.paidValue)}</div>
                  <div className="text-xs text-purple-500 mt-1">{stats.paidInvoices || 0} paid invoices</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium">Customers</div>
                  <div className="text-2xl font-bold text-amber-700">{stats.totalCustomers || 0}</div>
                  <div className="text-xs text-amber-500 mt-1">+{stats.newCustomersThisMonth || 0} this month</div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'sales_pipeline':
        return (
          <Card key={widgetId} className="col-span-1 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                Sales Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Quotes</span>
                  <span className="font-medium">{formatCurrency(stats.quotesValue)}</span>
                </div>
                <Progress value={Math.min((stats.totalQuotes || 0) * 10, 100)} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Orders</span>
                  <span className="font-medium">{formatCurrency(stats.ordersValue)}</span>
                </div>
                <Progress value={Math.min((stats.totalOrders || 0) * 15, 100)} className="h-2 bg-emerald-100" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Invoiced</span>
                  <span className="font-medium">{formatCurrency(stats.invoicesValue)}</span>
                </div>
                <Progress value={Math.min((stats.totalInvoices || 0) * 20, 100)} className="h-2 bg-purple-100" />
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Conversion Rate</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {stats.totalQuotes ? Math.round((stats.totalOrders / stats.totalQuotes) * 100) : 0}%
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'revenue':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-700">{formatCurrency(stats.paidValue)}</div>
              <p className="text-sm text-gray-500 mt-1">Total collected</p>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Outstanding</span>
                  <span className="font-medium text-amber-600">
                    {formatCurrency((stats.invoicesValue || 0) - (stats.paidValue || 0))}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Overdue</span>
                  <span className="font-medium text-red-600">{stats.overdueInvoices || 0} invoices</span>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'inventory_alerts':
        return (
          <Card key={widgetId}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Inventory Alerts
                </div>
                {(stats.criticalAlerts > 0 || stats.warningAlerts > 0) && (
                  <Badge variant="destructive" className="animate-pulse">
                    {(stats.criticalAlerts || 0) + (stats.warningAlerts || 0)} alerts
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary Badges */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="flex flex-col items-center p-2 bg-red-50 rounded-lg">
                  <span className="text-xl font-bold text-red-600">{stats.outOfStockItems || 0}</span>
                  <span className="text-xs text-red-700">Out of Stock</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-amber-50 rounded-lg">
                  <span className="text-xl font-bold text-amber-600">{stats.lowStockItems || 0}</span>
                  <span className="text-xs text-amber-700">Low Stock</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-green-50 rounded-lg">
                  <span className="text-xl font-bold text-green-600">
                    {(stats.totalInventoryItems || 0) - (stats.lowStockItems || 0) - (stats.outOfStockItems || 0)}
                  </span>
                  <span className="text-xs text-green-700">In Stock</span>
                </div>
              </div>
              
              {/* Critical Items List */}
              {inventoryAlerts.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {inventoryAlerts.slice(0, 5).map((alert, idx) => (
                    <div 
                      key={alert.id || idx} 
                      className={`flex items-center justify-between p-2 rounded-lg border ${
                        alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                        alert.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{alert.name}</p>
                        <p className="text-xs text-gray-500">{alert.sku}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <div className="text-right">
                          <span className={`text-sm font-bold ${
                            alert.current_quantity === 0 ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            {alert.current_quantity}
                          </span>
                          <span className="text-xs text-gray-400">/{alert.min_stock_level}</span>
                        </div>
                        <Badge 
                          variant={alert.severity === 'critical' ? 'destructive' : 'outline'}
                          className={`text-xs ${alert.severity === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-300' : ''}`}
                        >
                          {alert.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {inventoryAlerts.length > 5 && (
                    <p className="text-xs text-center text-gray-500 mt-2">
                      +{inventoryAlerts.length - 5} more items need attention
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">All inventory levels are healthy</p>
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="flex-1" onClick={() => onNavigate?.('inventory')}>
                  View Stock <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => onNavigate?.('suppliers')}>
                  <Truck className="h-4 w-4 mr-1" /> Order
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'team_performance':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Team
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-700">{stats.totalTeamMembers || 0}</div>
              <p className="text-sm text-gray-500 mt-1">Active team members</p>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Active Projects</span>
                  <span className="font-medium">{stats.activeProjects || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Completed</span>
                  <span className="font-medium text-green-600">{stats.completedProjects || 0}</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate?.('company-users')}>
                Manage Team <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      case 'recent_activity':
        return (
          <Card key={widgetId} className="col-span-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-600" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-3">
                  {recentActivity.length > 0 ? recentActivity.map((log) => (
                    <div key={log.id || `activity-${log.created_at}`} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                      <div className={`p-1.5 rounded-full ${
                        log.action === 'create' ? 'bg-green-100 text-green-600' :
                        log.action === 'update' ? 'bg-blue-100 text-blue-600' :
                        log.action === 'delete' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        <Activity className="h-3 w-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{log.user?.name || 'User'}</span>
                          {' '}{log.action} {log.resource_type}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(log.created_at)}</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-400 text-center py-8">No recent activity</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        );

      case 'my_quotes':
      case 'my_targets':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                My Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Quotes Created</span>
                    <span className="font-medium">{stats.totalQuotes || 0}</span>
                  </div>
                  <Progress value={Math.min((stats.totalQuotes || 0) * 10, 100)} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Orders Won</span>
                    <span className="font-medium">{stats.totalOrders || 0}</span>
                  </div>
                  <Progress value={Math.min((stats.totalOrders || 0) * 15, 100)} className="h-2 bg-emerald-100" />
                </div>
                <div className="pt-2 border-t">
                  <div className="text-sm text-gray-500">Total Value</div>
                  <div className="text-xl font-bold text-emerald-600">{formatCurrency(stats.ordersValue)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'my_schedule':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                My Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm">Active Projects</span>
                  <Badge className="bg-purple-500">{stats.activeProjects || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm">Completed</span>
                  <Badge className="bg-green-500">{stats.completedProjects || 0}</Badge>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate?.('schedule')}>
                View Schedule <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      case 'production_queue':
      case 'pending_orders':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-orange-600" />
                Production Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-700">{stats.pendingOrders || 0}</div>
              <p className="text-sm text-gray-500 mt-1">Orders in production</p>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Orders</span>
                  <span className="font-medium">{stats.totalOrders || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Completed</span>
                  <span className="font-medium text-green-600">{stats.completedOrders || 0}</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate?.('sales-orders')}>
                View Orders <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      case 'inventory_status':
      case 'low_stock':
        return (
          <Card key={widgetId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Boxes className="h-5 w-5 text-blue-600" />
                Inventory Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-700">{stats.totalInventoryItems || 0}</div>
              <p className="text-sm text-gray-500 mt-1">Total items tracked</p>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-red-600">Out of Stock</span>
                  <Badge variant="destructive">{stats.outOfStockItems || 0}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-600">Low Stock</span>
                  <Badge className="bg-amber-500">{stats.lowStockItems || 0}</Badge>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate?.('inventory')}>
                Manage Inventory <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      case 'outstanding_invoices':
      case 'revenue_summary':
        return (
          <Card key={widgetId} className="col-span-1 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-green-600" />
                Invoice Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">{stats.paidInvoices || 0}</div>
                  <div className="text-xs text-green-600">Paid</div>
                  <div className="text-sm font-medium text-green-700 mt-1">{formatCurrency(stats.paidValue)}</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-700">{stats.unpaidInvoices || 0}</div>
                  <div className="text-xs text-amber-600">Pending</div>
                  <div className="text-sm font-medium text-amber-700 mt-1">
                    {formatCurrency((stats.invoicesValue || 0) - (stats.paidValue || 0))}
                  </div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">{stats.overdueInvoices || 0}</div>
                  <div className="text-xs text-red-600">Overdue</div>
                  <div className="text-sm font-medium text-red-700 mt-1">Action needed</div>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate?.('invoices')}>
                View All Invoices <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="role-dashboard">
      {/* Welcome Header with Time-Based Greeting */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {getTimeGreeting()}, {userPermissions?.user?.name?.split(' ')[0] || 'there'}!
            </h2>
            <div className="text-gray-600 mt-1 flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700">{roleName}</Badge>
              <span className="text-sm">{getRoleWelcomeMessage(roleId)}</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {actions.slice(0, 4).map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => onNavigate?.(action.action)}
                data-testid={`quick-action-${action.action}`}
              >
                <action.icon className="h-4 w-4 mr-2" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Role-Specific KPI Cards */}
      {renderRoleKPIs(roleId)}

      {userPermissions?.permissions?.quotes && userPermissions.permissions.quotes !== 'none' && <UnlinkedRequestsCounts token={token} testId="dashboard-unlinked-requests" />}
      {userPermissions?.permissions?.quotes && userPermissions.permissions.quotes !== 'none' && <NeedsPricingCard token={token} />}

      {/* Website page health (live builder pages with unpublished changes / failing SEO) */}
      <PageHealthAlerts token={localStorage.getItem('apollo_token')} onOpenPage={(slug, focus) => { sessionStorage.setItem('website_open_slug', slug); if (focus) sessionStorage.setItem('website_open_focus', JSON.stringify(focus)); onNavigate?.('pages'); }} />

      {/* Quick Actions for Mobile */}
      <div className="md:hidden grid grid-cols-2 gap-2">
        {actions.map((action, idx) => (
          <Button
            key={idx}
            variant="outline"
            size="sm"
            className="justify-start"
            onClick={() => onNavigate?.(action.action)}
          >
            <action.icon className="h-4 w-4 mr-2" />
            {action.label}
          </Button>
        ))}
      </div>

      {/* Mobile Installer Job Cards - Swipeable */}
      {roleId === 'role_installer' && todaysJobs.length > 0 && (
        <div className="md:hidden" data-testid="mobile-job-cards">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Today's Jobs ({todaysJobs.length})</h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={activeJobIndex === 0}
                onClick={() => setActiveJobIndex(Math.max(0, activeJobIndex - 1))}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="text-sm text-gray-500">{activeJobIndex + 1} / {todaysJobs.length}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={activeJobIndex === todaysJobs.length - 1}
                onClick={() => setActiveJobIndex(Math.min(todaysJobs.length - 1, activeJobIndex + 1))}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
          
          {/* Swipeable Job Card */}
          <div 
            className="overflow-hidden touch-pan-x"
            onTouchStart={(e) => {
              const touch = e.touches[0];
              e.currentTarget.dataset.startX = touch.clientX;
            }}
            onTouchEnd={(e) => {
              const startX = parseFloat(e.currentTarget.dataset.startX || 0);
              const endX = e.changedTouches[0].clientX;
              const diff = startX - endX;
              if (diff > 50 && activeJobIndex < todaysJobs.length - 1) {
                setActiveJobIndex(activeJobIndex + 1);
              } else if (diff < -50 && activeJobIndex > 0) {
                setActiveJobIndex(activeJobIndex - 1);
              }
            }}
          >
            {(() => {
              const job = todaysJobs[activeJobIndex];
              if (!job) return null;
              
              const jobTime = job.start_time ? new Date(job.start_time).toLocaleTimeString('en-US', {
                hour: 'numeric', minute: '2-digit', hour12: true
              }) : 'TBD';
              
              const fullAddress = [job.address, job.city, job.state].filter(Boolean).join(', ');
              const mapsUrl = fullAddress ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}` : null;
              
              return (
                <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-amber-600 text-white">
                        {job.event_type?.replace('_', ' ').toUpperCase() || 'JOB'}
                      </Badge>
                      <span className="text-lg font-bold text-amber-700">{jobTime}</span>
                    </div>
                    
                    <h4 className="text-xl font-bold text-gray-800 mb-3">{job.title || 'Untitled Job'}</h4>
                    
                    <div className="space-y-2 text-sm">
                      {job.customer_name && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{job.customer_name}</span>
                        </div>
                      )}
                      {job.customer_phone && (
                        <a href={`tel:${job.customer_phone}`} className="flex items-center gap-2 text-blue-600">
                          <Phone className="h-4 w-4" />
                          <span>{job.customer_phone}</span>
                        </a>
                      )}
                      {fullAddress && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <span className="truncate">{fullAddress}</span>
                        </div>
                      )}
                      {job.notes && (
                        <p className="text-gray-600 bg-white/60 p-2 rounded mt-2">
                          📝 {job.notes}
                        </p>
                      )}
                    </div>
                    
                    {/* Status indicator */}
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">Current Status:</span>
                        <Badge className={`text-xs ${
                          job.status === 'completed' ? 'bg-green-600' :
                          job.status === 'in_progress' ? 'bg-amber-600' :
                          job.status === 'en_route' ? 'bg-blue-600' :
                          'bg-gray-500'
                        } text-white`}>
                          {(job.status || 'scheduled').replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2 mt-3">
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-600 text-white py-3 rounded-lg font-semibold shadow-md active:bg-gray-700 transition-colors text-sm"
                        >
                          <Navigation className="h-4 w-4" />
                          Directions
                        </a>
                      )}
                      
                      {/* Status action button */}
                      {(() => {
                        const action = getNextStatusAction(job.status || 'scheduled');
                        if (!action) return (
                          <div className="flex-1 flex items-center justify-center gap-2 bg-green-100 text-green-700 py-3 rounded-lg font-semibold text-sm">
                            <CheckCircle className="h-4 w-4" />
                            Completed
                          </div>
                        );
                        
                        const ActionIcon = action.icon;
                        return (
                          <button
                            onClick={() => updateJobStatus(job.id, action.next)}
                            disabled={updatingStatus}
                            className={`flex-1 flex items-center justify-center gap-2 ${action.color} text-white py-3 rounded-lg font-semibold shadow-md active:opacity-90 transition-colors text-sm disabled:opacity-50`}
                          >
                            <ActionIcon className="h-4 w-4" />
                            {updatingStatus ? 'Updating...' : action.label}
                          </button>
                        );
                      })()}
                    </div>

                    {/* Photo upload section */}
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-gray-500 mb-2">Job Photos</p>
                      <div className="flex gap-2">
                        <label className="flex-1 flex items-center justify-center gap-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-200 transition-colors">
                          <Camera className="h-4 w-4" />
                          Before
                          <input 
                            type="file" 
                            accept="image/*" 
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                uploadJobPhoto(job.id, e.target.files[0], 'before');
                              }
                            }}
                            disabled={uploadingPhoto}
                          />
                          {jobPhotos[job.id]?.before?.length > 0 && (
                            <Badge className="ml-1 h-4 px-1 text-xs bg-blue-500">{jobPhotos[job.id].before.length}</Badge>
                          )}
                        </label>
                        <label className="flex-1 flex items-center justify-center gap-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-200 transition-colors">
                          <Image className="h-4 w-4" />
                          After
                          <input 
                            type="file" 
                            accept="image/*" 
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                uploadJobPhoto(job.id, e.target.files[0], 'after');
                              }
                            }}
                            disabled={uploadingPhoto}
                          />
                          {jobPhotos[job.id]?.after?.length > 0 && (
                            <Badge className="ml-1 h-4 px-1 text-xs bg-green-500">{jobPhotos[job.id].after.length}</Badge>
                          )}
                        </label>
                      </div>
                      {uploadingPhoto && (
                        <p className="text-xs text-center text-gray-500 mt-1">Uploading...</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
          
          {/* Job indicators */}
          <div className="flex justify-center gap-1.5 mt-3">
            {todaysJobs.map((_, idx) => (
              <button
                key={idx}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  idx === activeJobIndex ? 'bg-amber-600' : 'bg-gray-300'
                }`}
                onClick={() => setActiveJobIndex(idx)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Route Map for Installer - Always render for installer role (has own empty state) */}
      {roleId === 'role_installer' && (
        <div className="md:hidden mt-6">
          <InstallerRouteMap date={new Date().toISOString().split('T')[0]} />
        </div>
      )}

      {/* Customize Dashboard Button */}
      <div className="flex justify-end mb-2">
        <Button variant="outline" size="sm" onClick={() => setShowCustomize(!showCustomize)} className="text-neutral-500" data-testid="customize-dashboard-btn">
          <Settings2 className="w-4 h-4 mr-1.5" /> Customize
        </Button>
      </div>

      {/* Customize Panel */}
      {showCustomize && (
        <Card className="mb-4 border-amber-200 bg-amber-50/30" data-testid="customize-panel">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-neutral-700">Toggle & reorder dashboard sections</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={resetLayout} className="text-xs">Reset Default</Button>
                <Button variant="outline" size="sm" onClick={() => setShowCustomize(false)} className="text-xs"><XIcon className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {sectionOrder.filter(id => {
                const sec = DASHBOARD_SECTIONS[id];
                return sec && (sec.roles.includes('all') || sec.roles.includes(roleId));
              }).map(id => {
                const sec = DASHBOARD_SECTIONS[id];
                const Icon = sec.icon;
                const hidden = hiddenSections.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleSection(id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${hidden ? 'bg-neutral-100 text-neutral-400 border-neutral-200' : 'bg-white text-neutral-700 border-amber-300'}`}
                    data-testid={`toggle-section-${id}`}
                  >
                    {hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3 text-amber-600" />}
                    <Icon className="w-3 h-3" />
                    {sec.label}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drag-and-drop Dashboard Sections */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sectionOrder.filter(id => !hiddenSections.includes(id))} strategy={verticalListSortingStrategy}>
          {sectionOrder.filter(id => !hiddenSections.includes(id)).map(sectionId => {
            const sec = DASHBOARD_SECTIONS[sectionId];
            if (!sec) return null;
            if (!sec.roles.includes('all') && !sec.roles.includes(roleId)) return null;

            return (
              <SortableSection key={sectionId} id={sectionId}>
                {sectionId === 'quick_stats' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                    <AdminQuickStats stats={stats} loading={loading} onNavigate={onNavigate} />
                    {/* Quick Actions inline */}
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">Quick Actions</h3>
                      <div className="flex flex-wrap gap-2">
                        {actions.map(a => (
                          <Button key={a.label} variant="outline" size="sm" onClick={() => onNavigate?.(a.action)} className="gap-2" data-testid={`quick-action-${a.action}`}>
                            <a.icon className="w-4 h-4" /> {a.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {sectionId === 'quick_actions' && null /* Merged into quick_stats */}
                {sectionId === 'widget_grid' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    {widgets.map(widgetId => renderWidget(widgetId))}
                  </div>
                )}
                {sectionId === 'leaderboard' && (
                  <div className="mt-6">
                    <TechnicianLeaderboard />
                  </div>
                )}
                {sectionId === 'smart_scheduling' && (
                  <div className="mt-6">
                    <SmartScheduling onSelectSlot={(slot) => {
                      toast.success(`Selected ${slot.time} on ${slot.date} with ${slot.installer_name}`);
                      onNavigate?.('schedule');
                    }} />
                  </div>
                )}
                {sectionId === 'revenue' && (
                  <div className="mt-6"><RevenueDashboard token={token} /></div>
                )}
                {sectionId === 're_engagement' && (
                  <div className="mt-6"><SmartReEngagement token={token} /></div>
                )}
                {sectionId === 'review_tracker' && (
                  <div className="mt-6"><ReviewTracker token={token} /></div>
                )}
              </SortableSection>
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
}
